import { useCallback, useEffect, useRef, useState } from "react";
import { IndofastLogo } from "../../components/layout/IndofastLogo";
import "../../styles/location-capture.css";

// Extract token from /location?token=<token>
const PAGE_TOKEN = new URLSearchParams(window.location.search).get("token") || "";
const BACKEND = (process.env.REACT_APP_API_BASE_URL || "").replace(/\/+$/, "");
const APP_API_KEY = process.env.REACT_APP_API_KEY || "";

// ── Internal API calls (no session — use API key) ────────────────────────────

async function webhookPost(path, data) {
  const res = await fetch(`${BACKEND}${path}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-API-Key": APP_API_KEY,
    },
    body: JSON.stringify(data),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw Object.assign(
      new Error(json?.detail || json?.error || json?.msg || `Error ${res.status}`),
      { status: res.status }
    );
  }
  return json;
}

// ── Google Maps loader ───────────────────────────────────────────────────────

let _mapsPromise = null;
function loadMaps() {
  if (window.google?.maps) return Promise.resolve();
  if (_mapsPromise) return _mapsPromise;
  const key = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "";
  if (!key) return Promise.reject(new Error("no-maps-key"));
  _mapsPromise = new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = `https://maps.googleapis.com/maps/api/js?key=${key}&libraries=places`;
    s.onload = resolve;
    s.onerror = () => { _mapsPromise = null; reject(new Error("Failed to load Google Maps")); };
    document.head.appendChild(s);
  });
  return _mapsPromise;
}

// Returns { address, city, state, country, postal_code } from geocoder result
function getAddressDetails(lat, lng) {
  return new Promise((resolve) => {
    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ location: { lat, lng } }, (results, status) => {
      if (status !== "OK" || !results[0]) {
        resolve({ address: "", city: "", state: "", country: "", postal_code: "" });
        return;
      }
      const result = results[0];
      const get = (...types) => {
        const comp = result.address_components.find((c) =>
          types.some((t) => c.types.includes(t))
        );
        return comp ? comp.long_name : "";
      };
      resolve({
        address: result.formatted_address,
        city: get("locality", "sublocality_level_1", "administrative_area_level_3"),
        state: get("administrative_area_level_1"),
        country: get("country"),
        postal_code: get("postal_code"),
      });
    });
  });
}

function getBrowserLocation() {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(Object.assign(new Error("no-support"), { code: -1 }));
      return;
    }
    navigator.geolocation.getCurrentPosition(resolve, reject, {
      enableHighAccuracy: true,
      timeout: 15000,
    });
  });
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function formatDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("en-GB", {
    day: "2-digit", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
}

// ── Sub-components ───────────────────────────────────────────────────────────

function Spinner({ size = 36 }) {
  return (
    <span
      className="lcp-spinner"
      style={{ width: size, height: size, borderWidth: size > 30 ? 3 : 2 }}
    />
  );
}

function LcpHeader({ ticketNumber }) {
  return (
    <header className="lcp-header">
      <div className="lcp-header-brand">
        <IndofastLogo variant="dark" height={22} />
      </div>
      {ticketNumber && (
        <div className="lcp-header-ticket">
          <span className="lcp-ticket-label">Ticket No :</span>
          <span className="lcp-ticket-no">{ticketNumber}</span>
        </div>
      )}
    </header>
  );
}

function TicketCard({ ticket }) {
  // Only show agent details once the backend has confirmed lat/lng (location is saved)
  const locationConfirmed = !!(ticket?.latitude && ticket?.longitude);
  return (
    <div className="lcp-ticket-card">
      <p className="lcp-ticket-card-title">Emergency Response Team: Technician Assignment</p>
      <div className="lcp-ticket-meta">
        <div className="lcp-meta-col">
          <span className="lcp-meta-label">Created Date</span>
          <span className="lcp-meta-value">{formatDate(ticket.created_at)}</span>
        </div>
        <div className="lcp-meta-col">
          <span className="lcp-meta-label">Executive details</span>
          {locationConfirmed && ticket.agent_name ? (
            <span className="lcp-meta-value">
              {ticket.agent_name}
              {ticket.agent_phone && (
                <a href={`tel:${ticket.agent_phone}`} className="lcp-phone-link">
                  <PhoneIcon />
                  {ticket.agent_phone}
                </a>
              )}
            </span>
          ) : (
            <span className="lcp-meta-value lcp-phone-empty"><PhoneIcon /></span>
          )}
        </div>
      </div>
      <p className="lcp-note">Note: This map page will be auto refreshed, please wait for a while.</p>
    </div>
  );
}

function PhoneIcon({ size = 15 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M6.62 10.79c1.44 2.83 3.76 5.15 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z" />
    </svg>
  );
}

// ── Address confirm modal ─────────────────────────────────────────────────────

function ConfirmModal({ address, onOk, onCancel }) {
  return (
    <div className="lcp-overlay">
      <div className="lcp-modal">
        <p className="lcp-modal-text">
          Please confirm your address:<br />
          <strong>{address || "Detecting your location…"}</strong>
        </p>
        <div className="lcp-modal-actions">
          <button className="lcp-modal-btn lcp-modal-btn--ghost" onClick={onCancel}>Cancel</button>
          <button className="lcp-modal-btn lcp-modal-btn--ok" onClick={onOk}>OK</button>
        </div>
      </div>
    </div>
  );
}

// ── Manual map picker ─────────────────────────────────────────────────────────

function ManualMapPicker({ onSave, onClose }) {
  const mapEl = useRef(null);
  const markerRef = useRef(null);
  const autocompleteRef = useRef(null);
  const inputRef = useRef(null);
  const [pos, setPos] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function init() {
      await loadMaps();
      if (cancelled || !mapEl.current) return;

      // Default to India if geolocation is not available
      let center = { lat: 20.5937, lng: 78.9629 };
      let zoom = 5;
      try {
        const p = await getBrowserLocation();
        center = { lat: p.coords.latitude, lng: p.coords.longitude };
        zoom = 15;
      } catch {}

      if (cancelled) return;

      const map = new window.google.maps.Map(mapEl.current, {
        center, zoom,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: false,
      });

      const marker = new window.google.maps.Marker({
        map, position: center, draggable: true, animation: window.google.maps.Animation.DROP,
      });
      markerRef.current = marker;
      setPos(center);

      marker.addListener("dragend", () => {
        const p = marker.getPosition();
        setPos({ lat: p.lat(), lng: p.lng() });
      });

      map.addListener("click", (e) => {
        const newPos = { lat: e.latLng.lat(), lng: e.latLng.lng() };
        marker.setPosition(newPos);
        setPos(newPos);
      });

      // Places Autocomplete
      if (inputRef.current) {
        const ac = new window.google.maps.places.Autocomplete(inputRef.current, {
          types: ["geocode"],
        });
        autocompleteRef.current = ac;
        ac.addListener("place_changed", () => {
          const place = ac.getPlace();
          if (place.geometry?.location) {
            const loc = {
              lat: place.geometry.location.lat(),
              lng: place.geometry.location.lng(),
            };
            map.panTo(loc);
            map.setZoom(16);
            marker.setPosition(loc);
            setPos(loc);
          }
        });
      }
    }

    init();
    return () => { cancelled = true; };
  }, []);

  async function handleSave() {
    if (!pos || saving) return;
    setSaving(true);
    try {
      const details = window.google?.maps
        ? await getAddressDetails(pos.lat, pos.lng)
        : { address: "", city: "", state: "", country: "", postal_code: "" };
      await onSave({ lat: pos.lat, lng: pos.lng, ...details });
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="lcp-manual">
      <div className="lcp-manual-header">
        <span className="lcp-manual-title">Enter Your Location</span>
        <button className="lcp-close-btn" onClick={onClose} aria-label="Close">✕</button>
      </div>
      <div className="lcp-manual-search">
        <input
          ref={inputRef}
          type="text"
          placeholder="Enter a location"
          className="lcp-search-input"
        />
      </div>
      <div ref={mapEl} className="lcp-map-el" />
      <div className="lcp-manual-footer">
        <button className="lcp-save-btn" onClick={handleSave} disabled={!pos || saving}>
          {saving ? "Saving…" : "Save Location"}
        </button>
      </div>
    </div>
  );
}

// ── Static map after assignment ───────────────────────────────────────────────

const BIKE_ICON_URL = `${process.env.PUBLIC_URL}/bike-icon.svg`;
const BIKE_ICON_ASPECT = 587 / 1179; // width / height of bike-icon.svg

// Strip the default InfoWindow chrome (bubble arrow, padding, close button)
// so the ETA badge reads as a plain floating pill, once per page load.
let etaBadgeStyleInjected = false;
function ensureEtaBadgeStyle() {
  if (etaBadgeStyleInjected) return;
  etaBadgeStyleInjected = true;
  const style = document.createElement("style");
  style.textContent = `
    .lcp-assigned-map .gm-style-iw-c {
      padding: 0 !important;
      background: transparent !important;
      box-shadow: none !important;
      border: none !important;
      outline: none !important;
      border-radius: 999px !important;
    }
    .lcp-assigned-map .gm-style-iw-d { overflow: visible !important; padding: 0 !important; }
    .lcp-assigned-map .gm-style-iw-ch { padding-top: 0 !important; }
    .lcp-assigned-map .gm-style-iw-t::after,
    .lcp-assigned-map .gm-style-iw-tc { display: none !important; }
    .lcp-assigned-map button.gm-ui-hover-effect { display: none !important; }
    .lcp-assigned-map .gm-style-iw,
    .lcp-assigned-map .gm-style-iw *,
    .lcp-assigned-map .gm-style-iw *:focus {
      outline: none !important;
      border: none !important;
    }
    .lcp-assigned-map .lcp-eta-badge { box-shadow: 0 4px 14px rgba(0,0,0,0.22) !important; }
  `;
  document.head.appendChild(style);
}

function AssignedMap({ lat, lng, agentLat, agentLng }) {
  const mapEl = useRef(null);
  const hasAgent = typeof agentLat === "number" && typeof agentLng === "number" &&
    !Number.isNaN(agentLat) && !Number.isNaN(agentLng);

  useEffect(() => {
    let cancelled = false;
    async function init() {
      await loadMaps();
      if (cancelled || !mapEl.current) return;
      const dest = { lat, lng };
      const map = new window.google.maps.Map(mapEl.current, {
        center: dest, zoom: 16,
        mapTypeControl: false, streetViewControl: false, fullscreenControl: false,
      });

      // Rider's captured location (red pin)
      new window.google.maps.Marker({ map, position: dest });

      if (!hasAgent) return;
      const origin = { lat: agentLat, lng: agentLng };
      const bikeHeight = 46;
      const bikeWidth = Math.round(bikeHeight * BIKE_ICON_ASPECT);

      // Technician's current location — bike icon marker
      const agentMarker = new window.google.maps.Marker({
        map, position: origin,
        icon: {
          url: BIKE_ICON_URL,
          scaledSize: new window.google.maps.Size(bikeWidth, bikeHeight),
          anchor: new window.google.maps.Point(bikeWidth / 2, bikeHeight),
        },
        zIndex: 999,
      });

      const bounds = new window.google.maps.LatLngBounds();
      bounds.extend(origin);
      bounds.extend(dest);
      map.fitBounds(bounds, 60);

      const directionsService = new window.google.maps.DirectionsService();
      const directionsRenderer = new window.google.maps.DirectionsRenderer({
        map,
        suppressMarkers: true,
        polylineOptions: { strokeColor: "#7c3aed", strokeWeight: 5 },
      });

      directionsService.route(
        { origin, destination: dest, travelMode: window.google.maps.TravelMode.DRIVING },
        (result, status) => {
          if (cancelled || status !== "OK") return;
          directionsRenderer.setDirections(result);
          const leg = result.routes[0]?.legs?.[0];
          if (leg) {
            ensureEtaBadgeStyle();
            new window.google.maps.InfoWindow({
              content: `<div class="lcp-eta-badge" style="font:600 11px/1 sans-serif;display:flex;align-items:center;gap:6px;padding:8px 8px;background:#fff;color:#111827;border-radius:999px;white-space:nowrap;">⏱️ ${leg.duration.text}</div>`,
              pixelOffset: new window.google.maps.Size(0, -bikeHeight - 6),
              disableAutoPan: true,
            }).open({ map, anchor: agentMarker });
          }
        }
      );
    }
    init();
    return () => { cancelled = true; };
  }, [lat, lng, agentLat, agentLng, hasAgent]);

  return <div ref={mapEl} className="lcp-assigned-map" />;
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function LocationCapturePage() {
  // step: 'init' | 'confirm' | 'manual' | 'saving' | 'waiting' | 'assigned' | 'error'
  const [step, setStep] = useState("init");
  const [ticket, setTicket] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");
  const [addrDetails, setAddrDetails] = useState({ address: "", city: "", state: "", country: "", postal_code: "" });
  const [savedPos, setSavedPos] = useState(null);
  const pollRef = useRef(null);

  // ── Poll for agent assignment (5 s interval, stops when agent_name arrives) ──
  const startPolling = useCallback(() => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const data = await webhookPost("/api/internals/ticket-location-webhook/verify-token/", {
          token: PAGE_TOKEN,
        });
        // Only advance state once backend confirms lat/lng (location truly saved)
        if (data?.latitude && data?.longitude) {
          clearInterval(pollRef.current);
          setTicket(data);
          setStep(data?.agent_name ? "assigned" : "waiting");
        } else {
          setTicket((prev) => ({ ...prev, ...data }));
        }
      } catch {}
    }, 5000);
  }, []);

  // Cleanup poll on unmount
  useEffect(() => () => clearInterval(pollRef.current), []);

  // ── Bootstrap: verify token + maps + geolocation ──────────────────────────
  useEffect(() => {
    if (!PAGE_TOKEN) {
      setErrorMsg("Invalid link. No token found.");
      setStep("error");
      return;
    }

    let cancelled = false;

    async function run() {
      // Step 1: verify token
      let ticketData;
      try {
        ticketData = await webhookPost("/api/internals/ticket-location-webhook/verify-token/", {
          token: PAGE_TOKEN,
        });
      } catch (err) {
        if (!cancelled) {
          setErrorMsg(err.message || "Invalid or expired link.");
          setStep("error");
        }
        return;
      }

      if (cancelled) return;
      setTicket(ticketData);

      // If location already saved → skip capture, show assigned or waiting
      if (ticketData.is_ticket_location) {
        if (ticketData.latitude && ticketData.longitude) {
          setSavedPos({ lat: Number(ticketData.latitude), lng: Number(ticketData.longitude) });
          loadMaps().catch(() => null); // load maps in background so AssignedMap can render
        }
        if (ticketData.agent_name) {
          setStep("assigned");
        } else {
          setStep("waiting");
          startPolling();
        }
        return;
      }

      // Step 2: try geolocation (max 8 s) → confirm dialog; on any failure → manual picker
      try {
        const [, pos] = await Promise.all([
          loadMaps().catch(() => null),
          Promise.race([
            getBrowserLocation(),
            new Promise((_, reject) =>
              setTimeout(() => reject(new Error("geo-timeout")), 8000)
            ),
          ]),
        ]);

        if (cancelled) return;
        const lat = pos.coords.latitude;
        const lng = pos.coords.longitude;
        setSavedPos({ lat, lng });

        const details = window.google?.maps
          ? await getAddressDetails(lat, lng)
          : { address: "", city: "", state: "", country: "", postal_code: "" };
        if (cancelled) return;
        setAddrDetails(details);
        setStep("confirm");
      } catch {
        if (!cancelled) setStep("manual");
      }
    }

    run();
    return () => { cancelled = true; };
  }, [startPolling]);

  // ── After save: start polling ─────────────────────────────────────────────
  function afterSave(ticketId, pos) {
    if (pos) setSavedPos(pos);
    setStep("waiting");
    startPolling();
  }

  // ── Save with auto-detected address ───────────────────────────────────────
  async function handleConfirmOk() {
    if (!ticket || !savedPos) return;
    setStep("saving");
    try {
      await webhookPost("/api/internals/ticket-location-webhook/save-ticket-location/", {
        ticket_id: ticket.ticket_id,
        latitude: savedPos.lat,
        longitude: savedPos.lng,
        ...addrDetails,
      });
      afterSave(ticket.ticket_id, null);
    } catch (err) {
      setErrorMsg(err.message || "Failed to save location. Please try again.");
      setStep("error");
    }
  }

  // ── Save from manual map ──────────────────────────────────────────────────
  async function handleManualSave({ lat, lng, address, city, state, country, postal_code }) {
    if (!ticket) return;
    setStep("saving");
    try {
      await webhookPost("/api/internals/ticket-location-webhook/save-ticket-location/", {
        ticket_id: ticket.ticket_id,
        latitude: lat,
        longitude: lng,
        address,
        city,
        state,
        country,
        postal_code,
      });
      afterSave(ticket.ticket_id, { lat, lng });
    } catch (err) {
      setErrorMsg(err.message || "Failed to save location. Please try again.");
      setStep("error");
    }
  }

  // ── Technician's current location (from agent_location.current_lat/lng) ───
  const agentLoc = ticket?.agent_location;
  const agentLat = agentLoc?.current_lat != null ? Number(agentLoc.current_lat) : null;
  const agentLng = agentLoc?.current_lng != null ? Number(agentLoc.current_lng) : null;

  // ── Floating call button (visible once agent is assigned) ─────────────────
  // Show call button only once location is confirmed on backend (lat/lng present)
  const callBtn = (ticket?.latitude && ticket?.longitude && ticket?.agent_phone) ? (
    <a href={`tel:${ticket.agent_phone}`} className="lcp-fab-call" aria-label="Call agent">
      <PhoneIcon size={24} />
    </a>
  ) : null;

  // ── Renders ────────────────────────────────────────────────────────────────
  if (step === "error") {
    return (
      <div className="lcp-page">
        <LcpHeader ticketNumber={ticket?.ticket_number} />
        <div className="lcp-center">
          <span style={{ fontSize: 48 }}>⚠️</span>
          <p className="lcp-center-title">Something went wrong</p>
          <p className="lcp-center-sub">{errorMsg}</p>
        </div>
      </div>
    );
  }

  if (step === "init" || step === "saving") {
    return (
      <div className="lcp-page">
        <LcpHeader ticketNumber={ticket?.ticket_number} />
        {ticket && <TicketCard ticket={ticket} />}
        <div className="lcp-center">
          <Spinner />
          <p className="lcp-center-sub">
            {step === "saving" ? "Saving your location…" : "Verifying your link…"}
          </p>
        </div>
      </div>
    );
  }

  if (step === "manual") {
    return (
      <div className="lcp-page">
        <LcpHeader ticketNumber={ticket?.ticket_number} />
        {ticket && <TicketCard ticket={ticket} />}
        <ManualMapPicker
          onSave={handleManualSave}
          onClose={() => setStep(savedPos ? "confirm" : "manual")}
        />
      </div>
    );
  }

  // "waiting" — location saved, no agent yet
  if (step === "waiting") {
    return (
      <div className="lcp-page">
        <LcpHeader ticketNumber={ticket?.ticket_number} />
        {ticket && <TicketCard ticket={ticket} />}
        {savedPos && <AssignedMap lat={savedPos.lat} lng={savedPos.lng} />}
        <div className="lcp-center" style={{ flex: savedPos ? "0 0 auto" : "1", paddingTop: 20, paddingBottom: 20 }}>
          <Spinner size={28} />
          <p className="lcp-center-sub">Location saved. Assigning nearest technician…</p>
        </div>
      </div>
    );
  }

  // "assigned" — agent confirmed
  if (step === "assigned") {
    return (
      <div className="lcp-page">
        <LcpHeader ticketNumber={ticket?.ticket_number} />
        <TicketCard ticket={ticket} />
        {savedPos && (
          <AssignedMap lat={savedPos.lat} lng={savedPos.lng} agentLat={agentLat} agentLng={agentLng} />
        )}
        {callBtn}
      </div>
    );
  }

  // step === 'confirm'
  return (
    <div className="lcp-page">
      <LcpHeader ticketNumber={ticket?.ticket_number} />
      {ticket && <TicketCard ticket={ticket} />}
      <ConfirmModal
        address={addrDetails.address}
        onOk={handleConfirmOk}
        onCancel={() => setStep("manual")}
      />
    </div>
  );
}
